import { useState } from 'react';
import { actions, useAppState } from '../state/store';
import type { HistoryEntry } from '../state/store';
import { formatNumber, relativeTime } from '../lib/format';
import { IconDownload, IconRefresh, IconStudio, IconTrash } from '../components/Icons';
import { toast } from '../components/Toaster';

interface HistoryViewProps {
  /** Load the entry's clip back into the studio stage. May re-fetch it first. */
  onOpen: (entry: HistoryEntry) => void | Promise<void>;
  /** Copy the entry's prompt + settings into the composer. */
  onReuse: (entry: HistoryEntry) => void;
  /** Downloads the entry's clip. May re-fetch it first. */
  onDownload: (entry: HistoryEntry) => void | Promise<void>;
  /** Whether Open/Download are even worth attempting for this entry. */
  canRecover: (entry: HistoryEntry) => boolean;
  onGoToStudio: () => void;
}

export function HistoryView({
  onOpen,
  onReuse,
  onDownload,
  canRecover,
  onGoToStudio,
}: HistoryViewProps) {
  const { history } = useAppState();
  // Entries currently being re-fetched from the engine (Open/Download can take
  // a few seconds for a large 1080p clip) — tracked to show a busy state
  // instead of a dead-looking button.
  const [pending, setPending] = useState<Set<string>>(new Set());

  const totalSpent = history.reduce((sum, h) => sum + h.cost, 0);

  const runPending = async (id: string, action: () => void | Promise<void>) => {
    setPending((prev) => new Set(prev).add(id));
    try {
      await action();
    } finally {
      setPending((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  return (
    <>
      <div className="wrap view-head">
        <span className="eyebrow">History</span>
        <h1 className="view-title">
          Everything you have <b>rendered</b>.
        </h1>
        <p className="view-lead">
          {history.length === 0
            ? 'Finished clips collect here.'
            : `${history.length} clips · ${formatNumber(totalSpent)} credits spent. Clips made by a local engine (like Maestro) stay downloadable from here indefinitely — the app re-fetches the file from the engine's own output folder as needed.`}
        </p>
        {history.length > 0 && (
          <div>
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={() => {
                actions.clearHistory();
                toast('History cleared.');
              }}
            >
              <IconTrash size={14} /> Clear history
            </button>
          </div>
        )}
      </div>

      <div className="wrap">
        {history.length === 0 ? (
          <div className="empty">
            <IconStudio size={30} />
            <span className="empty-title">No clips yet</span>
            <span className="empty-sub">
              Head to the studio, write a prompt, and run your first generation.
            </span>
            <button type="button" className="btn btn-primary" onClick={onGoToStudio}>
              Go to the studio
            </button>
          </div>
        ) : (
          <div className="history-grid">
            {history.map((entry) => {
              const recoverable = canRecover(entry);
              const busy = pending.has(entry.id);
              return (
                <article key={entry.id} className="history-card">
                  <div className="history-thumb">
                    {entry.thumbUrl ? (
                      <img src={entry.thumbUrl} alt="" />
                    ) : (
                      <span className="th-empty">no thumbnail</span>
                    )}
                    <span className="th-badge">
                      {entry.mode === 'image-to-video' ? 'IMG → VID' : 'TXT → VID'}
                    </span>
                  </div>

                  <div className="history-body">
                    <p className="history-prompt" title={entry.prompt}>
                      {entry.prompt || '—'}
                    </p>
                    <div className="history-meta">
                      <span>{entry.settings.resolution}</span>
                      <span>{entry.settings.steps} steps</span>
                      <span style={{ color: 'var(--accent-2)' }}>−{entry.cost} cr</span>
                      <span>{relativeTime(entry.createdAt)}</span>
                    </div>
                    {!recoverable && (
                      <span className="ledger-time">
                        Clip no longer available — re-run it with the same settings.
                      </span>
                    )}
                  </div>

                  <div className="history-actions">
                    {recoverable ? (
                      <>
                        <button
                          type="button"
                          className="btn btn-sm"
                          style={{ flex: 1 }}
                          disabled={busy}
                          onClick={() => void runPending(entry.id, () => onOpen(entry))}
                        >
                          {busy ? 'Fetching…' : 'Open'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-ghost"
                          aria-label="Download"
                          title="Download"
                          disabled={busy}
                          onClick={() => void runPending(entry.id, () => onDownload(entry))}
                        >
                          <IconDownload size={14} />
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm"
                        style={{ flex: 1 }}
                        onClick={() => onReuse(entry)}
                      >
                        <IconRefresh size={14} /> Load settings
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-ghost"
                      aria-label="Delete"
                      title="Delete"
                      onClick={() => actions.removeHistory(entry.id)}
                    >
                      <IconTrash size={14} />
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
